import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useForm } from "react-hook-form";
import authFetch from "../utils/authFetch";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import {
  calculateAge,
  calculateLocalDOB,
  currentYear,
} from "../shared/utils";
import { capitalizeFirstLetter } from "../shared/utils";

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import { PADITHI_OPTIONS } from "../utils/options";
import { PadithiEnum, PredictionType } from "../utils/enums";
import { UserCharts } from "./user-charts";
import CharaDasha from "./chara-dasha/CharaDasha";
import Tabs from "./tabs/Tabs";

const TestTab = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setValue,
    clearErrors,
  } = useForm();

  const [validationData, setValidationData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userError, setUserError] = useState("");

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Refs to prevent duplicate API calls during React StrictMode
  const hasFetchedUsers = useRef(false);
  const [padithi, setPadithi] = useState(PadithiEnum.LAL_KITAB);

  // Tabs
  const tabs = useMemo(() => ['Check Validation', 'View Chara Dasha'])
  const [activeTab, setActiveTab] = useState(0)

  const handleDeleteUser = (userId) => {
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await authFetch.delete(
        `${process.env.REACT_APP_BACKEND_URL}/user/admin/sandbox-customer/${userToDelete}`
      );
      setUsers((prev) => prev.filter((u) => u._id !== userToDelete));
      if (watch("user") === userToDelete) setValue("user", "");
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to delete user");
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const renderSelectedUser = (selectedId) => {
    if (!selectedId) return <em>Select a user</em>;
    const user = users.find((u) => u._id === selectedId);
    if (!user) return null;
    const userName = capitalizeFirstLetter(user?.name);
    const age = calculateAge(user?.birthDetails?.date);
    const runningAge = age + 1;
    const dob = calculateLocalDOB(
      user.birthDetails?.date,
      user.birthDetails?.utcOffset
    );
    return `${userName} - ${user.birthDetails?.place?.name || "Unknown"
      } (${runningAge} years, DOB: ${dob || "?"}, ${user.userCurrentTimezone || "N/A"
      })`;
  };

  useEffect(() => {
    const fetchAllUsers = async () => {
      if (hasFetchedUsers.current) {
        return;
      }
      try {
        hasFetchedUsers.current = true;
        setLoadingUsers(true);
        setUserError("");

        let allUsers = [];
        let page = 1;
        let hasMorePages = true;

        // Fetch all users by making multiple paginated requests
        while (hasMorePages) {
          const response = await authFetch.get(
            `${process.env.REACT_APP_BACKEND_URL}/user/admin/sandbox-customer?page=${page}&limit=100`
          );

          if (response.data.success && response.data.data.sandboxCustomers) {
            allUsers = [...allUsers, ...response.data.data.sandboxCustomers];
            const totalPages = response.data.data.meta?.totalPages || 1;
            hasMorePages = page < totalPages;
            page++;
          } else {
            hasMorePages = false;
          }
        }
        setUsers(allUsers);
        setUserError("");
        clearErrors("user");
      } catch (error) {
        console.error("Error fetching users:", error);
        if (
          error.response?.status !== 401 &&
          error.response?.data?.code !== 401 &&
          !(
            error.response?.status === 400 &&
            error.response?.data?.message === "jwt expired"
          )
        ) {
          setUserError(
            error.response?.data?.message || "Failed to fetch users"
          );
        } else {
          setUserError("");
          clearErrors("user");
        }
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchAllUsers();
  }, []);

  // Clear any existing form errors when component mounts
  useEffect(() => {
    clearErrors();
  }, []);

  const selectedUser = watch("user");
  const selectedYear = watch("year");
  const selectedMonth = watch("month");
  const selectedDay = watch("day");
  const selectedTimezone = watch("timezone");
  const laalkitaabValue = watch("lalkitab");

  const availableYears = Array.from(
    { length: 2040 - (currentYear - 1) + 1 },
    (_, i) => currentYear - 1 + i
  );

  const onSubmit = async (data) => {
    try {
      setValidationData(null);
      const userTimezone = users.find(
        (u) => u._id === data.user
      )?.userCurrentTimezone;

      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const requestData = {
        userId: data.user,
        year: data.year,
        conditionType: padithi,
        // conditionType:
        //   padithi === "lalkitab"
        //     ? "LAL_KITAB"
        //     : padithi === "nadi"
        //     ? "NADI"
        //     : "PARASHARA",
        userCurrentTimezone: userTimezone || browserTimezone,
      };

      // Add the appropriate condition field based on Padithi selection
      if (padithi === PadithiEnum.LAL_KITAB) {
        requestData.lalKitabCondition = data.lalkitab;
      } else if (padithi === PadithiEnum.NADI) {
        requestData.nadiCondition = data.lalkitab;
      } else if (padithi === PadithiEnum.PARASHARA) {
        requestData.parasharaCondition = data.lalkitab;
      }else if (padithi === PadithiEnum.JAIMINI) {
        requestData.jaiminiCondition = data.lalkitab;
      }
      const response = await authFetch.post(
        "/worker/admin/condition/validate",
        requestData,
        {
          headers: {
            "x-user-timezone": browserTimezone,
          },
        }
      );

      const { code, success, message, data: resData } = response.data;

      // Handle different response types
      if (typeof resData === "boolean") {
        setValidationData({
          type: "boolean",
          value: resData,
          message,
        });
      } else if (code === 400 && typeof resData === "string") {
        setValidationData({
          type: "html",
          value: resData,
          message,
        });
      } else if (resData && resData.isValid === true && resData.intervals) {
        // Handle case when isValid is true and there are intervals
        setValidationData({
          type: "intervals",
          value: resData.intervals,
          // message: "Rule applied successfully with intervals",
          expectedAt: resData.expectedAt || null,
        });
      } else if (resData && resData.isValid === false) {
        // Handle case when isValid is explicitly false
        setValidationData({
          type: "invalid",
          value: null,
          message: "No rules applied",
        });
      } else {
        setValidationData({
          type: "message",
          value: null,
          message,
        });
      }
    } catch (error) {
      // Don't show 401/400 jwt expired errors in the UI - they should be handled by token refresh
      if (
        error.response?.status === 401 ||
        error.response?.data?.code === 401 ||
        (error.response?.data?.code === 400 &&
          error.response?.data?.message === "jwt expired")
      ) {
        console.error("JWT expired - token refresh should handle this");
        return;
      }

      setValidationData({
        type: "error",
        value: error.response?.data?.data || null,
        message: error.response?.data?.message || "Failed to validate rules",
      });
    }
  };

  useEffect(() => {
    if(validationData) setValidationData(null)
  },[padithi])

  return (
    <Box
      sx={{ bgcolor: "background.paper", p: 1, borderRadius: 1, boxShadow: 1 }}
    >
      {selectedUser &&
        <Box sx={{ mb: 1 }}>
          <UserCharts
            userId={selectedUser}
            user={users?.find(user => user._id === selectedUser)}
            padithi={padithi}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            selectedDay={selectedDay}
            selectedTimezone={selectedTimezone}
            predictionType={PredictionType.YEAR}
          />
        </Box>
      }


      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* User Dropdown */}
          <FormControl fullWidth error={!!errors.user}>
            <InputLabel id="user-label">Select User </InputLabel>
            {loadingUsers ? (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Loading users...
                </Typography>
              </Box>
            ) : userError ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                {userError}
              </Alert>
            ) : (
              <Select
                labelId="user-label"
                id="user"
                label="Select User *"
                {...register("user", {
                  required: "User selection is required",
                })}
                onOpen={() => setUserMenuOpen(true)}
                onClose={() => setUserMenuOpen(false)}
                onChange={(e) => setValue("user", e.target.value)}
                value={selectedUser || ""}
                renderValue={renderSelectedUser}
              >
                <MenuItem value="">
                  <em>Select a user</em>
                </MenuItem>
                {Array.isArray(users) && users.length > 0 ? (
                  users.map((user) => (
                    <MenuItem
                      key={user._id}
                      value={user._id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>
                        {`${capitalizeFirstLetter(user.name || user.email)} - ${user.birthDetails?.place?.name || "Unknown"
                          } (${(calculateAge(user.birthDetails?.date) || 0) + 1
                          } years, DOB: ${calculateLocalDOB(
                            user.birthDetails?.date,
                            user.birthDetails?.utcOffset
                          ) || "?"
                          }, ${user.userCurrentTimezone || "N/A"})`}
                      </span>
                      {userMenuOpen && (
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteUser(user._id);
                          }}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      )}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No users available</MenuItem>
                )}
              </Select>
            )}
            {errors.user && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {errors.user.message}
              </Typography>
            )}
          </FormControl>

          {/* Year Dropdown */}
          <FormControl fullWidth>
            <InputLabel id="year-label">Year </InputLabel>
            <Select
              labelId="year-label"
              id="year"
              label="Year "
              {...register("year")}
              disabled={!selectedUser}
              onChange={(e) => setValue("year", e.target.value)}
              value={selectedYear || currentYear}
            >
              <MenuItem value="">
                <em>Select a year</em>
              </MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Padithi Dropdown */}
          <FormControl fullWidth>
            <InputLabel id="padithi-label">Padithi</InputLabel>
            <Select
              labelId="padithi-label"
              id="padithi"
              label="Padithi"
              {...register("padithi")}
              value={padithi}
              disabled={!selectedUser}
              onChange={(e) => setPadithi(e.target.value)}
            >
              {PADITHI_OPTIONS?.map(({ label, value }, i) =>
                <MenuItem key={i} value={value}>{label}</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Rule Input - Dynamic based on Padithi selection */}

          {padithi === PadithiEnum.JAIMINI &&
            <Tabs
              tabs={tabs}
              activeIndex={activeTab}
              onChange={(i) => setActiveTab(i)}
            />
          }
          {padithi !== PadithiEnum.JAIMINI || activeTab === 0 ?
            <TextField
              id="laalkitaab"
              label={
                padithi === PadithiEnum.NADI
                  ? "Nadi Rule"
                  : padithi === PadithiEnum.PARASHARA
                    ? "Parashara Rule"
                    : padithi === PadithiEnum.JAIMINI
                      ? "Jaimini Rule"
                      : "Lalkitab Rule"
              }
              {...register("lalkitab")}
              error={!!errors.lalkitab}
              helperText={errors.lalkitab?.message}
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              disabled={!padithi}
            />
            :
            <CharaDasha
              userId={selectedUser}
              user={users?.find(user => user._id === selectedUser)}
              padithi={padithi}
            />
          }
          {(padithi !== PadithiEnum.JAIMINI || activeTab === 0) &&
            <Button
              type="submit"
              variant="contained"
              disabled={
                isSubmitting ||
                !selectedUser ||
                !padithi ||
                !laalkitaabValue?.trim()
              }
              sx={{ py: 1.5, width: 200 }}
            >
              {isSubmitting ? "Processing..." : "Run Validation"}
            </Button>
          }
        </Box>
      </form>
      {(padithi !== PadithiEnum.JAIMINI || activeTab === 0 ) && validationData && (
        <>
          {(validationData.type === "html" ||
            validationData.type === "error") && (
              <Typography variant="body1" fontWeight="bold" sx={{ mt: 3, mb: 2 }}>
                {validationData.message}
              </Typography>
            )}

          {validationData.type === "boolean" && !validationData.value && (
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ mt: 3, mb: 2, pl: 2 }}
            >
              No applied rules found
            </Typography>
          )}

          {validationData.type === "invalid" && (
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ mt: 3, mb: 2, color: "#f44336", pl: 2 }}
            >
              No rules applied
            </Typography>
          )}

          {validationData.type === "intervals" && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="h6"
                sx={{ mb: 2, color: "#4caf50", fontWeight: "bold" }}
              >
                {validationData.message}
              </Typography>

              {validationData.expectedAt && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Expected at: {validationData.expectedAt}
                </Typography>
              )}

              <Typography
                variant="body1"
                sx={{ mb: 1, fontWeight: "medium", pl: 2 }}
              >
                Intervals:
              </Typography>
              <Box
                sx={{
                  maxHeight: "600px",
                  overflowY: "auto",
                  mb: 2,
                }}
              >
                {validationData.value.map((interval, idx) => (
                  <Typography
                    key={idx}
                    variant="body1"
                    sx={{
                      py: 1.5,
                      pl: 3,
                      borderBottom:
                        idx < validationData.value.length - 1
                          ? "1px solid #eee"
                          : "none",
                    }}
                  >
                    {typeof interval === "string"
                      ? interval
                      : `${interval.start} - ${interval.end}`}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {(validationData.type === "html" ||
            validationData.type === "error") &&
            validationData.value && (
              <Box sx={{ mt: 2 }}>
                <div
                  dangerouslySetInnerHTML={{ __html: validationData.value }}
                />
              </Box>
            )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteUser}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: "error.main" }}>
          Delete User
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this user? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteUser} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestTab;
